import { ContextSelector } from "@appretio/context-selector";
import { FeedbackRestoreBanner } from "@appretio/feedback";
import { ContextSyncWarning, formatDataAsOf } from "@appretio/shared";
import {
  Button,
  LoadingOverlay,
  Note,
  SelectGroup,
  Sheet,
  SingleSelect
} from "@appretio/ui-kit";
import clsx from "clsx";
import type { ReactNode } from "react";
import { ApplyButton } from "../components/applyButton/ApplyButton";
import { shownIntakeSources } from "../components/cubeDataTable/utils/intakeUtils";
import { MultiTableGrid } from "../components/multiTableGrid/MultiTableGrid";
import { StoreStatusFilter } from "../components/storeStatusFilter/StoreStatusFilter";
import { SummaryTableControls } from "../components/summaryTableControls/SummaryTableControls";
import { WaterfallGraph } from "../components/waterfallGraph/WaterfallGraph";
import { ContextViewSelector } from "../contextViews/ContextViewSelector";
import { FORECAST_CONTEXT_FIELDS } from "../forecastContext";
import {
  SUMMARY_PRODUCER_OPTIONS,
  type SummaryPageState,
  useSummaryPageState
} from "../hooks/useSummaryPageState";
import {
  contextActionsContainer,
  contextContainer,
  contextDimensions,
  contextRow,
  contextSelectorsContainer,
  controlsRow,
  dimSelectorsContainer,
  emptyState,
  graphControlsRow,
  gridFilterPending,
  gridReady,
  savedAtLabel,
  storeStatusContainer,
  summaryContent,
  summaryScrollArea,
  tableContent,
  versionSelectorsContainer
} from "../styles/pageLayout.css";
import { getDisplayLabel } from "../utils/summary/summaryPageHelpers";
import { useSummaryFeedbackRestore } from "./useSummaryFeedbackRestore";

/** Producer that a given summary page can write to. */
export type SummarySaveProducer = "user" | "budget";

interface SaveDisabledContext {
  isFrozen: boolean;
  producerMismatch: boolean;
  hasAppliedFilters: boolean;
  hasGridData: boolean;
  isContextDirty: boolean;
  isFetching: boolean;
  isSaving: boolean;
}

function getSaveDisabledReason(ctx: SaveDisabledContext): string | undefined {
  if (ctx.isFrozen) return "This budget is frozen — unfreeze to make changes.";
  if (ctx.producerMismatch)
    return "Save is disabled because the selected producer does not match this page's write scope.";
  if (!ctx.hasAppliedFilters) return "Apply filters before saving.";
  if (!ctx.hasGridData) return "No grid data to save.";
  if (ctx.isContextDirty) return "Apply the updated context before saving.";
  if (ctx.isFetching) return "Loading — wait for the grid to finish fetching.";
  if (ctx.isSaving) return "Save in progress.";
  return undefined;
}

/** Props for the shared summary page. */
export interface SummaryPageProps {
  /**
   * Producer whose rows are write-targetable from this page. Forecast
   * summary is ``"user"``; budget summary is ``"budget"``. The Save
   * button and cell-editability both gate on matching this producer
   * (and additionally, ``isFrozen`` being false — trivially true for
   * user cubes; the real gate for budget).
   */
  saveProducer: SummarySaveProducer;
  /**
   * Optional banner rendered above the grid when the selected budget
   * is frozen. Forecast summary pages omit this; budget summary passes
   * a ``<FrozenBanner />`` (or similar).
   */
  renderFrozenBanner?: (state: SummaryPageState) => ReactNode;
}

/**
 * Shared layout for the forecast-summary and budget-summary pages.
 *
 * Owns the identical chrome (context selector + producer/version/year
 * triple + index triple + dim triple + store-status filter, table
 * controls, optional waterfall graph, and the multi-table grid). The
 * two pages differ only at:
 *
 * - Save button disabled gate: depends on ``saveProducer`` and
 *   ``isFrozen`` (the latter is always false for user cubes but
 *   real for budget).
 * - Cell editability: same gate as Save.
 * - Frozen-cube banner: optional, injected via
 *   {@link SummaryPageProps.renderFrozenBanner}.
 */
export function SummaryPage({
  saveProducer,
  renderFrozenBanner
}: SummaryPageProps) {
  const state = useSummaryPageState({
    page: saveProducer === "budget" ? "budget-summary" : "forecast-summary"
  });
  // Everything the grid/graph display is derived from this snapshot, not
  // the live selectors — those can drift between Applies while the data
  // on screen stays from the last Apply.
  const applied = state.appliedFilters;
  const frozenBanner = renderFrozenBanner?.(state);
  const feedbackRestore = useSummaryFeedbackRestore(state);
  // The producer this page is *authorised* to write to, mirroring the
  // temporal grid's ``editableProducer`` convention: ``"user"`` or
  // ``"budget"`` when the scope is mutable, ``undefined`` when a
  // frozen budget makes the page read-only.
  const editableProducer: SummarySaveProducer | undefined = state.isFrozen
    ? undefined
    : saveProducer;
  // Editable when the selected producer matches the write scope *and*
  // the grid is in a stable state — no pending fetch/save, applied
  // filters not drifting. Drives both the Save button's disabled state
  // and ``onCellValueChanged`` on the multi-table grid.
  const canEdit =
    editableProducer === state.producer &&
    !!state.appliedFilters &&
    !!state.gridData &&
    !state.isSaving &&
    !state.isFetching &&
    !state.isContextDirty;

  const saveDisabledReason = canEdit
    ? undefined
    : getSaveDisabledReason({
        isFrozen: state.isFrozen,
        producerMismatch: editableProducer !== state.producer,
        hasAppliedFilters: !!state.appliedFilters,
        hasGridData: !!state.gridData,
        isContextDirty: state.isContextDirty,
        isFetching: state.isFetching,
        isSaving: state.isSaving
      });

  const sourceErrors = state.hasSourceError ? state.missingSources : [];

  return (
    <>
      <Sheet>
        <div className={contextContainer}>
          <div className={contextActionsContainer}>
            <ApplyButton
              onApply={state.handleApply}
              hasUnsavedEdits={state.grid.hasUnsavedEdits}
              onSaveFirst={state.canSaveFirst ? state.handleSave : undefined}
              disabled={state.isFetching || state.isSaving}
              isLoading={state.isFetching}
            />
            <Button
              variant="secondary"
              size="xs"
              onClick={state.handleSave}
              disabled={!canEdit}
              isLoading={state.isSaving}
              title={saveDisabledReason}
            >
              Save
            </Button>
          </div>
          <div className={contextSelectorsContainer}>
            <div className={contextRow}>
              <div className={contextDimensions}>
                <ContextSelector
                  fields={FORECAST_CONTEXT_FIELDS}
                  selected={state.selectedContextOptions}
                  setSelected={state.setSelectedContextOptions}
                  baseContextOptions={state.baseContextOptions}
                  filteredContextOptions={
                    state.hasActiveFilters ||
                    state.selectedStoreStatuses.length > 0 ||
                    state.cubeId
                      ? state.mergedFilteredContextOptions
                      : undefined
                  }
                  isLoading={state.baseLoading || state.filteredLoading}
                />
              </div>
              <ContextViewSelector views={state.contextViews} />
            </div>
            <div className={controlsRow}>
              <div className={dimSelectorsContainer}>
                <SelectGroup>
                  <SingleSelect
                    options={state.tableDimOptions}
                    selectedOption={state.selectedTableDim}
                    onChange={state.setTableDim}
                    label="Table dim"
                    size="sm"
                    error={sourceErrors.includes("tableDim")}
                  />
                  <SingleSelect
                    options={state.rowDimOptions}
                    selectedOption={state.selectedRowDim}
                    onChange={state.setRowDim}
                    label="Row dim"
                    size="sm"
                    error={sourceErrors.includes("rowDim")}
                  />
                </SelectGroup>
              </div>
              <div className={versionSelectorsContainer}>
                <SelectGroup>
                  <SingleSelect
                    options={SUMMARY_PRODUCER_OPTIONS}
                    selectedOption={state.selectedProducer}
                    onChange={(value) => {
                      state.setProducer(value);
                      state.setVersion("");
                      state.setYear("");
                    }}
                    label="Producer"
                    size="sm"
                    error={sourceErrors.includes("producer")}
                  />
                  <SingleSelect
                    options={state.mainVersionOptions}
                    selectedOption={state.selectedVersion}
                    onChange={state.setVersion}
                    label="Version"
                    size="sm"
                    disabled={!state.producer}
                    error={sourceErrors.includes("version")}
                  />
                  <SingleSelect
                    options={state.yearOptions}
                    selectedOption={state.selectedYear}
                    onChange={state.setYear}
                    label="Year"
                    size="sm"
                    clearable={false}
                    disabled={!state.version}
                    error={sourceErrors.includes("year")}
                  />
                </SelectGroup>
              </div>
              <div className={versionSelectorsContainer}>
                <SelectGroup>
                  <SingleSelect
                    options={SUMMARY_PRODUCER_OPTIONS}
                    selectedOption={SUMMARY_PRODUCER_OPTIONS.find(
                      (option) => option.value === state.idxProducer
                    )}
                    onChange={(value) => {
                      state.setIdxProducer(value);
                      state.setIdxVersion("");
                      state.setIdxYear("");
                    }}
                    label="Index producer"
                    size="sm"
                  />
                  <SingleSelect
                    options={state.idxVersionOptions}
                    selectedOption={state.selectedIdxVersion}
                    onChange={state.setIdxVersion}
                    label="Index version"
                    size="sm"
                    disabled={!state.idxProducer}
                  />
                  <SingleSelect
                    options={state.idxYearOptions}
                    selectedOption={state.selectedIdxYear}
                    onChange={state.setIdxYear}
                    label="Index year"
                    size="sm"
                    clearable={false}
                    disabled={!state.idxVersion}
                  />
                </SelectGroup>
              </div>
              <div className={storeStatusContainer}>
                <StoreStatusFilter
                  options={state.storeStatusOptions ?? []}
                  selected={state.selectedStoreStatuses}
                  onChange={state.setSelectedStoreStatuses}
                  isLoading={state.storeStatusLoading}
                  relevantStatuses={state.relevantStoreStatuses}
                />
              </div>
              {state.grid.savedAt && (
                <div className={savedAtLabel}>
                  Saved @ {formatDataAsOf(state.grid.savedAt)}
                </div>
              )}
            </div>
          </div>
        </div>
      </Sheet>

      <Sheet
        overlay={
          state.isFetching ? (
            <LoadingOverlay aria-label="Loading summary data" />
          ) : undefined
        }
      >
        <div className={clsx(tableContent, summaryContent)}>
          {feedbackRestore.restore && (
            <FeedbackRestoreBanner
              restore={feedbackRestore.restore}
              notes={feedbackRestore.notes}
              applyNote={feedbackRestore.applyNote}
              onDismiss={feedbackRestore.dismiss}
            />
          )}
          {frozenBanner}
          {state.appliedFilters && state.isContextDirty && (
            <ContextSyncWarning changedFields={state.changedFields} />
          )}
          {state.grid.indexError && (
            <Note
              variant="warning"
              size="compact"
              text={state.grid.indexError}
            />
          )}
          <SummaryTableControls
            views={state.views}
            planBy={{
              value: state.grid.planBy,
              onChange: state.grid.setPlanBy,
              hasIndexData: state.grid.hasIndexData
            }}
            agg={{ value: state.grid.agg, onChange: state.grid.setAgg }}
            intake={{
              value: state.grid.intakeMode,
              onChange: state.grid.handleIntakeToggle,
              availableSources: state.grid.availableIntakeSources,
              sources: shownIntakeSources(saveProducer === "budget")
            }}
            graph={{
              visible: state.grid.graphVisible,
              onToggle: () => state.grid.setGraphVisible((v) => !v),
              hasIndexData: state.grid.hasIndexData
            }}
          />
          <div className={summaryScrollArea}>
            {state.grid.graphVisible && state.grid.hasIndexData && (
              <div className={graphControlsRow}>
                <SingleSelect
                  options={state.grid.graphMetricOptions}
                  selectedOption={state.grid.graphMetricOptions.find(
                    (option) => option.value === state.grid.graphMetric
                  )}
                  onChange={state.grid.setGraphMetric}
                  label="Metric"
                  size="sm"
                />
                <SingleSelect
                  options={state.grid.graphTableOptions}
                  selectedOption={state.grid.graphTableOptions.find(
                    (option) =>
                      option.value === String(state.grid.graphTableIndex)
                  )}
                  onChange={(value) =>
                    state.grid.setGraphTableIndex(Number(value))
                  }
                  label="Table"
                  size="sm"
                />
              </div>
            )}

            {state.grid.graphVisible && state.grid.hasIndexData && (
              <WaterfallGraph
                data={state.filteredData}
                rowLabels={
                  state.filteredRowLabelsPerTable[state.grid.graphTableIndex] ??
                  []
                }
                metric={state.grid.graphMetric}
                metricLabel={getDisplayLabel(
                  state.grid.graphMetric,
                  state.grid.metaData
                )}
                tableIndex={state.grid.graphTableIndex}
                tableLabel={state.tableLabels[state.grid.graphTableIndex] ?? ""}
                agg={state.grid.agg}
                rowDim={applied?.rowDim ?? state.rowDim}
                indexLabel={
                  applied &&
                  applied.idxProducer === applied.producer &&
                  applied.idxYear === applied.year
                    ? `${applied.idxProducer} - ${applied.idxYear} (index)`
                    : `${applied?.idxProducer} - ${applied?.idxYear}`
                }
                mainLabel={`${applied?.producer} - ${applied?.year}`}
              />
            )}

            {state.gridData &&
            state.tableLabels.length > 0 &&
            state.filteredRowLabelsPerTable.length > 0 ? (
              <div
                className={
                  state.grid.isFilterPending ? gridFilterPending : gridReady
                }
              >
                <MultiTableGrid
                  tableLabels={state.tableLabels}
                  rowLabelsPerTable={state.filteredRowLabelsPerTable}
                  data={state.filteredData}
                  columnDefs={state.grid.columnDefs}
                  rowLabelField="row_label"
                  onCellValueChanged={
                    canEdit ? state.grid.handleCellValueChanged : undefined
                  }
                  onMetricVisible={state.grid.setMetricVisible}
                />
              </div>
            ) : (
              !state.isFetching && (
                <div className={emptyState}>
                  Select a producer, version, and year, then click Apply.
                </div>
              )
            )}
          </div>
        </div>
      </Sheet>
    </>
  );
}
